import { useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { NativePasswordResetReturn } from "./native-password-reset-return";
import { getDesktopDeepLinkUrl } from "@/lib/desktop-deep-link";
import { AuthPageLayout } from "./auth-page-layout";
import { confirmPasswordReset } from "@/api/auth";
import { getAuthErrorMessage } from "@/hooks/use-auth";
import { Button, FormError, FormField, PasswordInput } from "@/components/ui";
import { useT } from "@/i18n";
import { authMessages } from "@/i18n/messages/auth";

export function ResetPasswordPage() {
  const t = useT(authMessages);
  const { uid, token } = useParams<{ uid: string; token: string }>();

  const [params] = useSearchParams();
  const context = params.get("desktop_context") ?? "";
  const [browser, setBrowser] = useState(false);
  const [nativeState, setNativeState] = useState("");
  const onNativeReady = useCallback((url: string) => {
    setNativeState(new URL(url).searchParams.get("state") ?? "");
  }, []);
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (password1 !== password2) {
      setError(t("passwordsDoNotMatch"));
      return;
    }

    if (!uid || !token) {
      setError(t("invalidResetLink"));
      return;
    }

    setIsSubmitting(true);
    try {
      await confirmPasswordReset({
        uid,
        token,
        new_password1: password1,
        new_password2: password2,
      });
      setSuccess(true);
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthPageLayout>
      <div className="auth-page-stack">
        <h2 className="auth-page-title" data-testid="reset-heading">
          {t("newPassword")}
        </h2>

        {success ? (
          <div className="auth-state-stack" data-testid="reset-success">
            <p className="auth-page-copy">{t("passwordResetDone")}</p>
            {nativeState ? (
              <a
                href={getDesktopDeepLinkUrl("sign-in", { state: nativeState })}
                rel="noreferrer"
                className="auth-small-link"
                data-testid="reset-return-native"
              >
                {t("returnToDenker")}
              </a>
            ) : (
              <Link
                to="/auth/login"
                className="auth-small-link"
                data-testid="back-to-login"
              >
                {t("signIn")}
              </Link>
            )}
          </div>
        ) : context && !browser && uid && token ? (
          <NativePasswordResetReturn
            uid={uid}
            token={token}
            context={context}
            onReady={onNativeReady}
            onBrowser={() => setBrowser(true)}
          />
        ) : (
          <>
            <FormError
              message={error}
              className="mt-0"
              data-testid="reset-error"
            />

            <form
              onSubmit={handleSubmit}
              className="auth-login-form flex flex-col"
              data-testid="reset-form"
            >
              <FormField label={t("password")} htmlFor="password1">
                <PasswordInput
                  id="password1"
                  required
                  autoComplete="new-password"
                  value={password1}
                  onChange={(e) => setPassword1(e.target.value)}
                  placeholder={t("password")}
                  className="auth-liquid-input h-12 rounded-[18px] text-appkit-subhead"
                  data-testid="reset-password1"
                />
              </FormField>

              <FormField label={t("confirm")} htmlFor="password2">
                <PasswordInput
                  id="password2"
                  required
                  autoComplete="new-password"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  placeholder={t("confirm")}
                  className="auth-liquid-input h-12 rounded-[18px] text-appkit-subhead"
                  data-testid="reset-password2"
                />
              </FormField>

              <Button
                type="submit"
                variant="secondary"
                size="lg"
                disabled={isSubmitting}
                className="auth-liquid-primary-button w-full font-semibold"
                data-testid="reset-submit"
              >
                {isSubmitting ? t("resetting") : t("reset")}
              </Button>
            </form>
          </>
        )}
      </div>
    </AuthPageLayout>
  );
}
